import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import { 
  List, Package, TrendingUp, Edit, XCircle, Clock, Eye, AlertTriangle, 
  ArrowLeft, RefreshCw, Plus, Gavel, CheckCircle, BarChart3, ImageOff,
  ChevronRight, Calendar, DollarSign, Timer
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyListingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [endingListingId, setEndingListingId] = useState(null);
  const [reactivatingListingId, setReactivatingListingId] = useState(null);
  const [extendingListingId, setExtendingListingId] = useState(null);
  const [newEndDate, setNewEndDate] = useState('');
  const [extendEndDate, setExtendEndDate] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/listings/user/${user.id}`);
      setListings(response.data);
    } catch (error) {
      console.error('Failed to fetch listings', error);
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const handleEndListing = async (e, listingId) => {
    e.preventDefault();
    e.stopPropagation();
    setEndingListingId(listingId);
  };

  const confirmEndListing = async () => {
    if (!endingListingId) return;
    
    try {
      await axios.patch(`${API_URL}/api/listings/${endingListingId}/status?status=closed`);
      toast.success('Listing ended successfully');
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end listing');
    } finally {
      setEndingListingId(null);
    }
  };

  const handleReactivateListing = (e, listingId) => {
    e.preventDefault();
    e.stopPropagation();
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7);
    setNewEndDate(defaultDate.toISOString().slice(0, 16));
    setReactivatingListingId(listingId);
  };

  const confirmReactivateListing = async () => {
    if (!reactivatingListingId || !newEndDate) return;
    
    try {
      await axios.post(`${API_URL}/api/listings/${reactivatingListingId}/reactivate`, {
        bidding_end_time: new Date(newEndDate).toISOString()
      });
      toast.success('Listing reactivated successfully!');
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate listing');
    } finally {
      setReactivatingListingId(null);
      setNewEndDate('');
    }
  };

  const handleExtendListing = (e, listingId, currentEndTime) => {
    e.preventDefault();
    e.stopPropagation();
    const defaultDate = new Date(currentEndTime);
    defaultDate.setDate(defaultDate.getDate() + 7);
    setExtendEndDate(defaultDate.toISOString().slice(0, 16));
    setExtendingListingId(listingId);
  };

  const confirmExtendListing = async () => {
    if (!extendingListingId || !extendEndDate) return;
    
    try {
      await axios.post(`${API_URL}/api/listings/${extendingListingId}/extend`, {
        new_end_time: new Date(extendEndDate).toISOString()
      });
      toast.success('Bidding time extended! Bidders will be notified.');
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend bidding time');
    } finally {
      setExtendingListingId(null);
      setExtendEndDate('');
    }
  };

  // Calculate stats
  const stats = {
    total: listings.length,
    live: listings.filter(l => l.status === 'live').length,
    closed: listings.filter(l => l.status === 'closed').length,
    inTrade: listings.filter(l => l.status === 'in_trade').length,
    completed: listings.filter(l => l.status === 'completed').length,
    totalBids: listings.reduce((sum, l) => sum + (l.bids_count || 0), 0),
  };

  // Filter listings based on active tab
  const filteredListings = activeTab === 'all' 
    ? listings 
    : listings.filter(l => l.status === activeTab);

  const getStatusColor = (status) => {
    const colors = {
      live: 'bg-emerald-500',
      closed: 'bg-slate-400',
      in_trade: 'bg-blue-500',
      completed: 'bg-indigo-500',
      cancelled: 'bg-red-500',
      removed: 'bg-red-500'
    };
    return colors[status] || 'bg-slate-400';
  };

  const getStatusBadge = (status) => {
    const styles = {
      live: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      closed: 'bg-slate-100 text-slate-600 border-slate-200',
      in_trade: 'bg-blue-100 text-blue-700 border-blue-200',
      completed: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200',
      removed: 'bg-red-100 text-red-700 border-red-200'
    };
    return styles[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const formatTimeRemaining = (endTime) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return { text: 'Ended', urgent: true };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return { text: `${days}d ${hours}h`, urgent: days < 2 };
    if (hours > 0) return { text: `${hours}h left`, urgent: hours < 12 };
    return { text: 'Ending soon', urgent: true };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const tabs = [
    { id: 'all', label: 'All', count: stats.total },
    { id: 'live', label: 'Live', count: stats.live },
    { id: 'in_trade', label: 'In Trade', count: stats.inTrade },
    { id: 'completed', label: 'Completed', count: stats.completed },
    { id: 'closed', label: 'Closed', count: stats.closed },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-2 text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Listings Dashboard
            </h1>
          </div>
          <Link
            to="/create-listing"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
          >
            <Plus className="w-5 h-5" />
            Create New Listing
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-slate-600" />
              </div>
              <span className="text-2xl font-bold text-slate-900">{stats.total}</span>
            </div>
            <p className="text-sm text-slate-500">Total Listings</p>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-emerald-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-2xl font-bold text-emerald-600">{stats.live}</span>
            </div>
            <p className="text-sm text-slate-500">Live Now</p>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-blue-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-2xl font-bold text-blue-600">{stats.inTrade}</span>
            </div>
            <p className="text-sm text-slate-500">In Trade</p>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-indigo-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-indigo-600" />
              </div>
              <span className="text-2xl font-bold text-indigo-600">{stats.completed}</span>
            </div>
            <p className="text-sm text-slate-500">Completed</p>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <XCircle className="w-5 h-5 text-slate-500" />
              </div>
              <span className="text-2xl font-bold text-slate-600">{stats.closed}</span>
            </div>
            <p className="text-sm text-slate-500">Closed</p>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Gavel className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">{stats.totalBids}</span>
            </div>
            <p className="text-sm text-amber-100">Total Bids</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {tab.label}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Listings */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
            <List className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {activeTab === 'all' ? 'No listings yet' : `No ${activeTab.replace('_', ' ')} listings`}
            </h3>
            <p className="text-slate-600 mb-6">
              {activeTab === 'all' ? 'Create your first listing to start trading' : 'Check other tabs or create a new listing'}
            </p>
            <Link
              to="/create-listing"
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700"
            >
              <Plus className="w-5 h-5" />
              Create Listing
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredListings.map((listing) => {
              const timeRemaining = formatTimeRemaining(listing.bidding_end_time);
              
              return (
                <div
                  key={listing.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-lg transition-all"
                  data-testid={`listing-card-${listing.id}`}
                >
                  <div className="flex flex-col sm:flex-row">
                    {/* Image */}
                    <Link 
                      to={`/listing/${listing.id}`}
                      className="sm:w-48 md:w-64 h-48 sm:h-auto flex-shrink-0 relative"
                    >
                      {listing.images && listing.images.length > 0 ? (
                        <img 
                          src={listing.images[0]} 
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                          <ImageOff className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                      {/* Status Badge on Image */}
                      <div className={`absolute top-3 left-3 w-3 h-3 rounded-full ${getStatusColor(listing.status)} ring-2 ring-white`} />
                      {listing.images && listing.images.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-lg">
                          +{listing.images.length - 1} more
                        </div>
                      )}
                    </Link>

                    {/* Content */}
                    <div className="flex-1 p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Link 
                              to={`/listing/${listing.id}`}
                              className="text-lg font-semibold text-slate-900 hover:text-indigo-600 transition-colors line-clamp-1"
                            >
                              {listing.title}
                            </Link>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusBadge(listing.status)}`}>
                              {listing.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <p className="text-slate-500 text-sm line-clamp-2">{listing.description}</p>
                        </div>
                      </div>

                      {/* Stats Row */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                        <div className="bg-slate-50 rounded-xl p-3">
                          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                            <Gavel className="w-3.5 h-3.5" />
                            Bids Received
                          </div>
                          <p className="text-lg font-bold text-indigo-600">{listing.bids_count || 0}</p>
                        </div>
                        
                        <div className="bg-slate-50 rounded-xl p-3">
                          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Listed On
                          </div>
                          <p className="text-sm font-semibold text-slate-700">{formatDate(listing.created_at)}</p>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-3">
                          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                            <Package className="w-3.5 h-3.5" />
                            Category
                          </div>
                          <p className="text-sm font-semibold text-slate-700 truncate">{listing.category}</p>
                        </div>

                        {listing.status === 'live' && (
                          <div className={`rounded-xl p-3 ${timeRemaining.urgent ? 'bg-orange-50' : 'bg-slate-50'}`}>
                            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                              <Clock className="w-3.5 h-3.5" />
                              Time Left
                            </div>
                            <p className={`text-sm font-bold ${timeRemaining.urgent ? 'text-orange-600' : 'text-slate-700'}`}>
                              {timeRemaining.text}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                        <Link
                          to={`/listing/${listing.id}`}
                          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View Details
                        </Link>
                        
                        {listing.status === 'live' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/edit-listing/${listing.id}`);
                              }}
                              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={(e) => handleExtendListing(e, listing.id, listing.bidding_end_time)}
                              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                            >
                              <Timer className="w-4 h-4" />
                              Extend Time
                            </button>
                            <button
                              onClick={(e) => handleEndListing(e, listing.id)}
                              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                            >
                              <XCircle className="w-4 h-4" />
                              End Listing
                            </button>
                          </>
                        )}
                        
                        {(listing.status === 'closed' || listing.status === 'cancelled') && (
                          <button
                            onClick={(e) => handleReactivateListing(e, listing.id)}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                          >
                            <RefreshCw className="w-4 h-4" />
                            Reactivate
                          </button>
                        )}

                        {listing.bids_count > 0 && (
                          <Link
                            to={`/listing/${listing.id}`}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors ml-auto"
                          >
                            <Gavel className="w-4 h-4" />
                            View {listing.bids_count} Bid{listing.bids_count > 1 ? 's' : ''}
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* End Listing Modal */}
        {endingListingId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">End Listing?</h3>
                  <p className="text-sm text-slate-500">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-slate-600 mb-6">
                All active bids will be cancelled and the listing will be marked as closed.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setEndingListingId(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmEndListing}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  End Listing
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reactivate Modal */}
        {reactivatingListingId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Reactivate Listing</h3>
                  <p className="text-sm text-slate-500">Set a new bidding end date</p>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  New End Date *
                </label>
                <input
                  type="datetime-local"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-emerald-500 h-12 px-4"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setReactivatingListingId(null);
                    setNewEndDate('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmReactivateListing}
                  disabled={!newEndDate}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  Reactivate
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Extend Bidding Time Modal */}
        {extendingListingId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Timer className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Extend Bidding Time</h3>
                  <p className="text-sm text-slate-500">Give bidders more time</p>
                </div>
              </div>
              
              <p className="text-slate-600 mb-4">
                Extend the bidding deadline to give more time for offers. All current bidders will be notified.
              </p>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  New End Date *
                </label>
                <input
                  type="datetime-local"
                  value={extendEndDate}
                  onChange={(e) => setExtendEndDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-amber-500 h-12 px-4"
                />
                <p className="text-xs text-slate-500 mt-1">Maximum extension: 14 days from now</p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setExtendingListingId(null);
                    setExtendEndDate('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmExtendListing}
                  disabled={!extendEndDate}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  Extend Bidding
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
